/**
 * Trainova AI — Platform Tenant List Page
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenants } from '../hooks/useTenants';
import { TenantTable } from '../components/TenantTable';
import { TenantForm } from '../components/TenantForm';
import { Tenant } from '../types';
import { AdminPageHeader } from '../components/AdminPageHeader';

export function TenantListPage() {
  const navigate = useNavigate();
  const { tenants, isLoading, error, createTenant, updateTenant } = useTenants();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSelectTenant = (tenant: Tenant) => {
    navigate(`/admin/tenants/${tenant.id}`);
  };

  const handleToggleStatus = async (tenant: Tenant) => {
    setActionError(null);
    const newStatus = tenant.isActive ? 'SUSPENDED' : 'ACTIVE';
    const res = await updateTenant(tenant.id, { status: newStatus });
    if (!res.success) {
      setActionError(res.error || 'Failed to update organization status.');
    }
  };

  const handleCreateSubmit = async (data: {
    name: string;
    slug: string;
    primaryColor: string;
    adminUser?: any;
  }) => {
    setIsSubmitting(true);
    setActionError(null);
    const res = await createTenant(data);
    setIsSubmitting(false);
    if (res.success) {
      setShowCreateModal(false);
    } else {
      setActionError(res.error || 'Failed to provision organization.');
    }
  };

  return (
    <div className="admin-page">
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Client Organizations' },
        ]}
        icon="🏢"
        title="Platform Organizations & Tenants"
        badge="Multi-Tenant Master"
        description="Manage enterprise multi-tenant organizations, inspect cross-tenant statistics, and provision new workspaces."
        actions={
          <button
            onClick={() => setShowCreateModal(true)}
            className="admin-btn admin-btn--primary"
            style={{
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              padding: '10px 18px',
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '13.5px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)',
            }}
          >
            <span style={{ fontSize: '16px', lineHeight: 1 }}>+</span> Provision Organization
          </button>
        }
      />

      {actionError && <div className="admin-alert admin-alert--error">{actionError}</div>}
      {error && <div className="admin-alert admin-alert--error">{error}</div>}

      {isLoading ? (
        <div style={{ background: '#FFFFFF', border: '1px solid var(--line, #E2E8F0)', borderRadius: '14px', padding: '60px 24px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
          <div className="dashboard-spinner" style={{ margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--muted, #64748B)', fontSize: '14px', fontWeight: 500, margin: 0 }}>
            Loading client organizations & tenant workspaces…
          </p>
        </div>
      ) : (
        <TenantTable
          tenants={tenants}
          onSelectTenant={handleSelectTenant}
          onToggleStatus={handleToggleStatus}
        />
      )}

      {showCreateModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <TenantForm
              onSubmit={handleCreateSubmit}
              onCancel={() => setShowCreateModal(false)}
              isLoading={isSubmitting}
            />
          </div>
        </div>
      )}
    </div>
  );
}
